import React, { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Platform, Share } from "react-native";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useFirebase } from "@components/firebase/useFirebase";
import { useAuth } from "@components/auth/useAuth";
import { getMyMedia, getMedia, editMedia, deleteMedia } from "@/services/firebase/media-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { getMyGroups } from "@/services/firebase/group-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { MediaModel, GroupModel, Contact, ShareModel, ILocation, ITagAssociationMeta } from "common247";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { CustomAlert } from "@/components/ThemedAlert";
import { SharingControl } from "@/components/sharing/SharingControl";
import { MediaEditor } from "@/components/media/MediaEditor";
import { ThemedKebabMenu, type IKebabActionItem } from "@/components/ThemedKebabMenu";
import { downloadMediaFile } from "@/utils/media-download";
import { navigateBackOrToList } from "@/utils/navigation";
import { entitySync } from "@/services/sync/entity-sync";
import { entityCache } from "@/services/sync/entity-cache";

/**
 * Media details and editor screen supporting metadata updates and sharing permissions.
 */
export default function MediaDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [media, setMedia] = useState<MediaModel | null>(null);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  const fetchAllData = async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const [mediaRes, sharesRes, contactsRes, groupsRes] = await Promise.all([
        getMyMedia(services, {}),
        getSharesForSource(services, { sourceId: id, sourceType: "media" }),
        getMyContacts(services, { includeInactive: false }),
        getMyGroups(services, {}),
      ]);

      let found = mediaRes.status ? mediaRes.media.find((m) => m.id === id) : null;
      if (!found) {
        // Fallback to getMedia for shared media items accessed via direct URL
        const singleRes = await getMedia(services, { mediaId: id });
        if (singleRes.status && singleRes.media) {
          found = Array.isArray(singleRes.media) ? singleRes.media[0] : singleRes.media;
        }
      }

      if (found) {
        setMedia(found);
      } else {
        CustomAlert.alert("Error", "Media not found.");
        navigateBackOrToList(router, "/(tabs)/my-media", navigation);
      }
      if (sharesRes.status) setShares(sharesRes.shares);
      if (contactsRes.status) {
        setContacts(
          (contactsRes.contacts || []).filter(
            (c) => c.id !== user?.uid && (!user?.uid || c.userId !== user.uid) && (!user?.email || c.email !== user.email),
          ),
        );
      }
      if (groupsRes.status) setGroups(groupsRes.groups || []);
    } catch (error) {
      console.error("Error fetching media details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [services, id]);

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !id) return;
    setIsLoadingShares(true);
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "media",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        const sharesRes = await getSharesForSource(services, { sourceId: id, sourceType: "media" });
        if (sharesRes.status) setShares(sharesRes.shares);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing media:", error);
    } finally {
      setIsLoadingShares(false);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !id) return;
    setIsLoadingShares(true);
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        const sharesRes = await getSharesForSource(services, { sourceId: id, sourceType: "media" });
        if (sharesRes.status) setShares(sharesRes.shares);
      }
    } catch (error) {
      console.error("Error unsharing media:", error);
    } finally {
      setIsLoadingShares(false);
    }
  };

  const handleSave = async (
    title: string,
    location?: ILocation,
    description?: string,
    favouriteRating?: number,
    tags?: Record<string, ITagAssociationMeta>,
    date?: string,
    contacts?: Record<string, any>,
    groups?: Record<string, any>,
    memories?: Record<string, any>,
    places?: Record<string, any>,
    anchors?: Record<string, any>
  ) => {
    if (!services || !id || !(title || "").trim()) return;
    setIsSaving(true);
    try {
      const payload: any = {
        mediaId: id,
        title: (title || "").trim(),
        name: (title || "").trim(),
        location,
        description: (description || "").trim(),
        favouriteRating: favouriteRating ?? 3,
        date,
      };
      if (tags !== undefined) {
        payload.tags = tags;
      }
      if (contacts !== undefined) {
        payload.contacts = contacts;
      }
      if (groups !== undefined) {
        payload.groups = groups;
      }
      if (memories !== undefined) {
        payload.memories = memories;
      }
      if (places !== undefined) {
        payload.places = places;
      }
      if (anchors !== undefined) {
        payload.anchors = anchors;
      }
      if (location && (location.latitude !== undefined || location.longitude !== undefined || location.text)) {
        payload.metadata = {
          ...(media?.metadata || {}),
          gpsLatitude: location.latitude,
          gpsLongitude: location.longitude,
          gpsLocationLabel: location.text,
        };
      }
      const res = await editMedia(services, payload);
      if (res.status) {
        const updatedMemoryItems = payload.memories !== undefined
          ? Object.keys(payload.memories).map((memId) => {
              const existing = (media?.memoryItems || []).find((it: any) => (it.id || it.memoryId) === memId);
              if (existing) return existing;
              const cached = entityCache.getCachedMemory(memId);
              return {
                id: memId,
                memoryId: memId,
                title: payload.memories[memId]?.displayName || cached?.title || "Memory",
                ...(cached || {}),
              };
            })
          : media?.memoryItems;

        const updatedMedia: MediaModel = {
          ...(media || ({} as MediaModel)),
          title: payload.title,
          name: payload.name,
          location: payload.location,
          description: payload.description,
          favouriteRating: payload.favouriteRating,
          date: payload.date,
          metadata: payload.metadata || media?.metadata,
          tags: payload.tags !== undefined ? payload.tags : media?.tags,
          contacts: payload.contacts !== undefined ? payload.contacts : media?.contacts,
          groups: payload.groups !== undefined ? payload.groups : media?.groups,
          memories: payload.memories !== undefined ? payload.memories : media?.memories,
          places: payload.places !== undefined ? payload.places : media?.places,
          anchors: payload.anchors !== undefined ? payload.anchors : media?.anchors,
          memoryItems: updatedMemoryItems,
        };
        // Auto-save updates in place — do not close the edited item
        setMedia(updatedMedia);
        entitySync.emit({
          type: "media",
          action: "update",
          id,
          data: updatedMedia,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update media.");
      }
    } catch (error) {
      console.error("Error updating media:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!services || !id) return;
    await confirmEntityDeletion({
      services,
      entityType: "media",
      entityId: id,
      entityName: media?.title,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deleteMedia(services, { mediaId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-media", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const userRole = media?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const canNativeShare = Platform.OS !== "web" || (typeof navigator !== "undefined" && !!navigator.share);

  const handleNativeShare = async () => {
    if (!media) return;
    try {
      const mediaTitle = media.title || "Media";
      const shareMessage = `${mediaTitle}: ${media.downloadUrl}`;
      await Share.share({
        title: mediaTitle,
        message: shareMessage,
        url: media.downloadUrl,
      });
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "Failed to share media.");
    }
  };

  const kebabActions: IKebabActionItem[] = [];
  if (canNativeShare) {
    kebabActions.push({
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleNativeShare,
    });
  }
  if (media?.downloadUrl) {
    kebabActions.push({
      id: "download",
      label: "Download",
      icon: "download-outline",
      onPress: () => downloadMediaFile(media.downloadUrl, media.title || "Media"),
    });
  }
  if (canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Media",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={media ? `${isViewer ? "View" : "Edit"}: ${media.title || "Media Details"}` : "Media Details"}
      loading={loading}
      rightComponent={kebabActions.length > 0 ? <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Media options" /> : undefined}
    >
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && media && (
          <View style={styles.container}>
            <ThemedView style={styles.card}>
              <MediaEditor
                media={media}
                isSaving={isSaving}
                editable={canEdit}
                contacts={contacts}
                groups={groups}
                onSave={handleSave}
                onCancel={() => navigateBackOrToList(router, "/(tabs)/my-media", navigation)}
                rightActions={
                  kebabActions.length > 0 ? (
                    <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Media options" />
                  ) : undefined
                }
              >
                <ThemedView style={styles.section}>
                  <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
                  <SharingControl
                    shares={shares}
                    contacts={contacts}
                    groups={groups}
                    canShare={media.canShare}
                    canUnshare={media.canShare}
                    isLoading={isLoadingShares}
                    onShare={handleShare}
                    onUnshare={handleUnshare}
                  />
                </ThemedView>
              </MediaEditor>
            </ThemedView>
          </View>
        )}
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  center: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    borderRadius: 0,
    overflow: "hidden",
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    padding: 12,
    marginBottom: 16,
  },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  memoriesSection: {
    marginBottom: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.05)",
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "bold",
    opacity: 0.4,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  memoryLink: {
    paddingVertical: 4,
  },
  memoryLinkText: {
    fontSize: 15,
    color: "#4A90E2",
  },
  footerActions: {
    marginTop: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    alignItems: "center",
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  backBtnText: {
    color: "#666",
    fontSize: 14,
  },
});
